// src/pages/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from "../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  LogOut, 
  Loader2, 
  Clock, 
  CheckCircle2, 
  XCircle,
  LayoutDashboard,
  RefreshCw,
  Calendar,
  Filter,
  X,
  Mail,
  Check,
  AlertCircle,
  Send,
  User,
  Cake,
  Gift,
  PartyPopper,
  Phone,
  Users,
  Copy,
  CalendarDays,
  FileSpreadsheet,
  FileText,
  Menu,
  ChevronDown,
  CalendarClock,
  UserCircle,
  Search,
  ArrowUpDown,
  Download,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Archive,
  CopyCheck
} from "lucide-react";
import ReservationTable from "../components/ReservationTable";
import BlockedDatesManager from "../components/BlockedDatesManager";
import DeletedReservationsModal from "../components/DeletedReservationsModal";
import DeletionReasonModal from "../components/DeletionReasonModal";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dateFilter, setDateFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [timeFilter, setTimeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [processingId, setProcessingId] = useState(null);
  const [emailStatus, setEmailStatus] = useState({});
  const [showEmailSuccess, setShowEmailSuccess] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [showBlockedDatesModal, setShowBlockedDatesModal] = useState(false);
  const [showCustomersModal, setShowCustomersModal] = useState(false);
  const [showDeletedModal, setShowDeletedModal] = useState(false);
  const [showDeletionReasonModal, setShowDeletionReasonModal] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [deletedReservations, setDeletedReservations] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  // Available time slots
  const timeSlots = [
    '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', 
    '10:00 PM', '11:00 PM', '12:00 AM', '1:00 AM'
  ];

  // Available months for filtering
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Available years
  const currentYear = new Date().getFullYear();
  const years = [
    currentYear,
    currentYear + 1,
    currentYear + 2
  ];

  // Restaurant info
  const restaurantInfo = {
    name: "HAI Beirut",
    location: "A Tower, Antelias Rd, Beirut",
    phone: "+961 70 882 206",
    email: "reservations@haibeirut.com",
    website: "https://haibeirut.com"
  };

  // ========== AUTHENTICATION CHECK ==========
  useEffect(() => {
    let isMounted = true;
    
    const checkAdminAuth = async () => {
      try {
        setAuthChecking(true);
        setAuthError(null);
        
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;
        
        if (!session) {
          if (isMounted) navigate('/admin-login', { replace: true });
          return;
        }

        const { data, error } = await supabase.functions.invoke('check-admin-auth', {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });

        if (error) throw new Error(error.message || 'Failed to verify admin status');

        if (!data || !data.authenticated || !data.isAdmin) {
          await supabase.auth.signOut();
          if (isMounted) navigate('/admin-login', { replace: true });
          return;
        }

        if (isMounted) setAdminUser(data.user);

      } catch (error) {
        if (isMounted) {
          setAuthError(error.message);
          setTimeout(() => {
            if (isMounted) navigate('/admin-login', { replace: true });
          }, 2000);
        }
      } finally {
        if (isMounted) setAuthChecking(false);
      }
    };

    checkAdminAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        if (isMounted) navigate('/admin-login', { replace: true });
      } else if (event === 'SIGNED_IN' && session) {
        checkAdminAuth();
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, [navigate]);

  // ========== FETCH CUSTOMERS ==========
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!adminUser) return;
      
      setLoadingCustomers(true);
      try {
        const { data, error } = await supabase
          .from('customers')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setCustomers(data || []);
      } catch (error) {
      } finally {
        setLoadingCustomers(false);
      }
    };

    fetchCustomers();
  }, [adminUser]);

  // ========== FETCH DELETED RESERVATIONS ==========
  useEffect(() => {
    const fetchDeletedReservations = async () => {
      if (!adminUser) return;
      
      setLoadingDeleted(true);
      try {
        const { data, error } = await supabase
          .from('deleted_reservations')
          .select('*')
          .order('deleted_at', { ascending: false });
        
        if (error) {
          if (error.code === '42501' || error.message?.includes('permission')) {
            setTestResult({
              success: false,
              message: ' Permission denied for deleted_reservations table. Please check RLS policies.'
            });
            setTimeout(() => setTestResult(null), 5000);
          }
          setDeletedReservations([]);
        } else {
          setDeletedReservations(data || []);
        }
      } catch (error) {
        setDeletedReservations([]);
      } finally {
        setLoadingDeleted(false);
      }
    };

    fetchDeletedReservations();
  }, [adminUser]);

  // ========== FETCH RESERVATIONS ==========
  const { data: reservations = [], isLoading: reservationsLoading, refetch } = useQuery({
    queryKey: ['reservations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reservations')
        .select(`
          *,
          customers (
            id,
            customer_name,
            email,
            phone,
            date_of_birth,
            created_at,
            updated_at
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      const transformedData = data.map(reservation => ({
        ...reservation,
        customer_name: reservation.customers?.customer_name || reservation.customer_name,
        email: reservation.customers?.email || reservation.email,
        phone: reservation.customers?.phone || reservation.phone,
        date_of_birth: reservation.customers?.date_of_birth || reservation.date_of_birth,
        customer_id: reservation.customers?.id || reservation.customer_id,
      }));
      
      return transformedData || [];
    },
    enabled: !!adminUser,
  });

  // ========== MUTATIONS ==========
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const { error } = await supabase
        .from('reservations')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
      return { id, data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });

  const updateTableNumberMutation = useMutation({
    mutationFn: async ({ id, table_number }) => {
      const { error } = await supabase
        .from('reservations')
        .update({ table_number })
        .eq('id', id);
      
      if (error) throw error;
      return { id, table_number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });

  // Soft delete mutation
  const deleteMutation = useMutation({
    mutationFn: async ({ id, reason }) => {
      if (!id) throw new Error('No reservation ID provided');
      
      // Get reservation data
      const { data: reservation, error: fetchError } = await supabase
        .from('reservations')
        .select('*, customers(*)')
        .eq('id', id)
        .maybeSingle();
      
      if (fetchError) throw new Error(`Failed to fetch reservation: ${fetchError.message}`);
      if (!reservation) throw new Error('Reservation not found');
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw new Error('Failed to get current user');
      
      // Insert into deleted_reservations
      const { error: insertError } = await supabase
        .from('deleted_reservations')
        .insert({
          original_reservation_id: reservation.id,
          customer_name: reservation.customers?.customer_name || reservation.customer_name,
          phone: reservation.customers?.phone || reservation.phone,
          email: reservation.customers?.email || reservation.email,
          date: reservation.date,
          time: reservation.time,
          guests: reservation.guests,
          special_requests: reservation.special_requests,
          status: reservation.status,
          user_id: reservation.user_id,
          created_at: reservation.created_at,
          updated_at: reservation.updated_at,
          date_of_birth: reservation.customers?.date_of_birth || reservation.date_of_birth,
          customer_id: reservation.customer_id,
          approved_at: reservation.approved_at,
          rejected_at: reservation.rejected_at,
          recaptcha_verified: reservation.recaptcha_verified,
          recaptcha_score: reservation.recaptcha_score,
          recaptcha_token: reservation.recaptcha_token,
          recaptcha_verified_at: reservation.recaptcha_verified_at,
          table_number: reservation.table_number,
          deleted_by: user?.id,
          deletion_reason: reason
        });
      
      if (insertError) throw insertError;
      
      // Delete from original table
      const { error: deleteError } = await supabase
        .from('reservations')
        .delete()
        .eq('id', id);
      
      if (deleteError) throw deleteError;
      
      return { id, reason };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      
      // Refresh deleted reservations
      const refreshDeleted = async () => {
        const { data } = await supabase
          .from('deleted_reservations')
          .select('*')
          .order('deleted_at', { ascending: false });
        setDeletedReservations(data || []);
      };
      refreshDeleted();
      
      setTestResult({
        success: true,
        message: " Reservation moved to deleted records"
      });
      setTimeout(() => setTestResult(null), 3000);
    },
    onError: (error) => {
      setTestResult({
        success: false,
        message: ` Failed to delete reservation: ${error.message}`
      });
      setTimeout(() => setTestResult(null), 3000);
    }
  });

  // Restore mutation
  const restoreMutation = useMutation({
    mutationFn: async (deletedReservation) => {
      const { error: insertError } = await supabase
        .from('reservations')
        .insert({
          id: deletedReservation.original_reservation_id,
          customer_name: deletedReservation.customer_name,
          phone: deletedReservation.phone,
          email: deletedReservation.email,
          date: deletedReservation.date,
          time: deletedReservation.time,
          guests: deletedReservation.guests,
          special_requests: deletedReservation.special_requests,
          status: deletedReservation.status,
          user_id: deletedReservation.user_id,
          created_at: deletedReservation.created_at,
          updated_at: new Date().toISOString(),
          customer_id: deletedReservation.customer_id,
          approved_at: deletedReservation.approved_at,
          rejected_at: deletedReservation.rejected_at,
          recaptcha_verified: deletedReservation.recaptcha_verified,
          recaptcha_score: deletedReservation.recaptcha_score,
          recaptcha_token: deletedReservation.recaptcha_token,
          recaptcha_verified_at: deletedReservation.recaptcha_verified_at,
          table_number: deletedReservation.table_number
        });
      
      if (insertError) throw insertError;
      
      const { error: deleteError } = await supabase
        .from('deleted_reservations')
        .delete()
        .eq('id', deletedReservation.id);
      
      if (deleteError) throw deleteError;
      
      return deletedReservation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      
      const refreshDeleted = async () => {
        const { data } = await supabase
          .from('deleted_reservations')
          .select('*')
          .order('deleted_at', { ascending: false });
        setDeletedReservations(data || []);
      };
      refreshDeleted();
      
      setTestResult({
        success: true,
        message: "Reservation restored successfully"
      });
      setTimeout(() => setTestResult(null), 3000);
    },
    onError: (error) => {
      setTestResult({
        success: false,
        message: ` Failed to restore reservation: ${error.message}`
      });
      setTimeout(() => setTestResult(null), 3000);
    }
  });

  // ========== LOGOUT ==========
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/admin-login', { replace: true });
    } catch (error) {
    }
  };

  // ========== EMAIL FUNCTIONS ==========
  const sendEmailViaEdgeFunction = async (to, subject, html) => {
    if (!to || to.trim() === '') {
      return { success: false, error: 'No email address provided' };
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to,
          subject,
          html,
          from: "HAÏ Beirut <reservations@haibeirut.com>"
        }
      });

      if (error) throw new Error(error.message || "Failed to send email");
      return { success: true, data };
    } catch (error) {
      throw error;
    }
  };

  const sendEmailMutation = useMutation({
    mutationFn: async ({ reservation, action }) => {
      if (!reservation.email || reservation.email.trim() === '') {
        return { success: false, error: 'No email address provided' };
      }

      const formattedDate = reservation.date 
        ? format(parseISO(reservation.date), "EEEE, MMMM d, yyyy")
        : 'Not specified';
      
      const subject = action === 'approve' 
        ? `Reservation Confirmed - ${restaurantInfo.name}`
        : `Reservation Update - ${restaurantInfo.name}`;
      
      const emailContent = action === 'approve' 
        ? generateApprovalEmail(reservation, formattedDate)
        : generateRejectionEmail(reservation, formattedDate);
      
      return await sendEmailViaEdgeFunction(
        reservation.email,
        subject,
        emailContent
      );
    },
    onSuccess: (data, variables) => {
      const { reservationId } = variables;
      if (data && data.success === false) {
        setEmailStatus(prev => ({
          ...prev,
          [reservationId]: { success: false, message: 'No email address provided' }
        }));
      } else {
        setEmailStatus(prev => ({
          ...prev,
          [reservationId]: { success: true, message: 'Email sent successfully!' }
        }));
        setShowEmailSuccess(true);
        setTimeout(() => setShowEmailSuccess(false), 3000);
      }
    },
    onError: (error, variables) => {
      const { reservationId } = variables;
      setEmailStatus(prev => ({
        ...prev,
        [reservationId]: { 
          success: false, 
          message: `Failed to send email: ${error.message}` 
        }
      }));
      setShowEmailError(true);
      setTimeout(() => setShowEmailError(false), 3000);
    }
  });

  // Copy email to clipboard
  const copyEmailToClipboard = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopiedEmail(email);
    setTimeout(() => setCopiedEmail(null), 2000);
  };

  // ========== UPDATED APPROVAL EMAIL WITH AGE & DRESS CODE ==========
  const generateApprovalEmail = (reservation, formattedDate) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Reservation Confirmation</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: linear-gradient(135deg, #2D6B47 0%, #4CAF50 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
          .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; }
          .reservation-details { background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin: 20px 0; }
          .detail-row { display: flex; justify-content: space-between; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #f0f0f0; }
          .detail-label { font-weight: bold; color: #2D6B47; }
          .success-badge { background: #4CAF50; color: white; padding: 5px 15px; border-radius: 20px; display: inline-block; font-weight: bold; }
          .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0; color: #666; font-size: 12px; }
          .contact-info { background: #f0f7f2; padding: 15px; border-radius: 8px; margin: 20px 0; }
          .important-note { background: #fff3cd; border-left: 4px solid #ffc107; padding: 15px; margin: 20px 0; border-radius: 8px; }
          .dress-code-box { background: #e8f4f8; border-left: 4px solid #2196F3; padding: 15px; margin: 20px 0; border-radius: 8px; }
          .dress-code-box h4 { color: #0d47a1; margin-top: 0; }
          .dress-code-box ul { margin: 10px 0 0 0; padding-left: 20px; }
          .dress-code-box li { margin-bottom: 5px; }
          .age-requirement { background: #fce4ec; border-left: 4px solid #e91e63; padding: 15px; margin: 20px 0; border-radius: 8px; }
          .age-requirement h4 { color: #880e4f; margin-top: 0; }
          .restriction-list { color: #d32f2f; font-weight: 500; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 style="margin: 0; font-size: 28px;">Reservation Confirmed!</h1>
          <p style="margin: 10px 0 0; opacity: 0.9;">Thank you for choosing ${restaurantInfo.name}</p>
        </div>
        
        <div class="content">
          <p>Dear <strong>${reservation.customer_name}</strong>,</p>
          <p>We are pleased to inform you that your reservation has been <span class="success-badge">CONFIRMED</span>.</p>
          
          <div class="reservation-details">
            <h3 style="color: #2D6B47; margin-top: 0;">Reservation Details</h3>
            
            <div class="detail-row">
              <span class="detail-label">Date:</span>
              <span>${formattedDate}</span>
            </div>
            
            <div class="detail-row">
              <span class="detail-label">Time:</span>
              <span>${reservation.time || 'Not specified'}</span>
            </div>
            
            <div class="detail-row">
              <span class="detail-label">Number of Guests:</span>
              <span>${reservation.guests || 'Not specified'} people</span>
            </div>
            
            ${reservation.table_number ? `
            <div class="detail-row">
              <span class="detail-label">Table Number:</span>
              <span>${reservation.table_number}</span>
            </div>
            ` : ''}
            
            ${reservation.special_requests ? `
            <div class="detail-row">
              <span class="detail-label">Special Requests:</span>
              <span>${reservation.special_requests}</span>
            </div>
            ` : ''}
          </div>

          <!-- AGE REQUIREMENT -->
          <div class="age-requirement">
            <h4>🔞 Age Requirement</h4>
            <p style="margin: 0;"><strong>19+</strong> is the standard age requirement for entry to the bar.</p>
            <p style="margin: 5px 0 0; font-size: 14px; color: #880e4f;">Valid ID may be required upon arrival.</p>
          </div>

          <!-- DRESS CODE -->
          <div class="dress-code-box">
            <h4>👔 Dress Code: Smart Casual</h4>
            <p style="margin: 5px 0;"><strong>Men:</strong> Long trousers/jeans, collared or smart shirt, and closed shoes.</p>
            <p style="margin: 5px 0;"><strong>Women:</strong> Smart/evening attire; avoid overly casual beachwear.</p>
            <p style="margin: 10px 0 0; font-weight: bold; color: #d32f2f;">❌ Please avoid:</p>
            <ul style="margin: 5px 0 0 20px; color: #d32f2f;">
              <li>Shorts</li>
              <li>Slippers / Flip-flops</li>
              <li>Tank tops</li>
              <li>Sportswear</li>
              <li>Beachwear</li>
            </ul>
          </div>
          
          <div class="contact-info">
            <h4 style="color: #2D6B47; margin-top: 0;">Restaurant Information</h4>
            <p><strong>${restaurantInfo.name}</strong></p>
            <p>📍 ${restaurantInfo.location}</p>
            <p>📞 ${restaurantInfo.phone}</p>
            <p>✉️ ${restaurantInfo.email}</p>
          </div>
          
          <div class="important-note">
            <h4 style="color: #856404; margin-top: 0;">Important Notes:</h4>
            <p>• Please arrive <strong>15 minutes</strong> before your reservation time</p>
            <p>• Tables are held for <strong>30 minutes</strong> maximum</p>
            <p>• For cancellations, please notify us at least <strong>60 minutes</strong> in advance</p>
            <p>• Valid ID required for age verification (19+)</p>
            <p>• Dress code strictly enforced</p>
          </div>
          
          <p>We look forward to welcoming you!</p>
          <p>Warm regards,<br><strong>The ${restaurantInfo.name} Team</strong></p>
        </div>
        
        <div class="footer">
          <p>This is an automated message. Please do not reply to this email.</p>
          <p>© ${new Date().getFullYear()} ${restaurantInfo.name}. All rights reserved.</p>
        </div>
      </body>
      </html>
    `;
  };

  // ========== REJECTION EMAIL (unchanged) ==========
  const generateRejectionEmail = (reservation, formattedDate) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Reservation Update</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: linear-gradient(135deg, #dc3545 0%, #e35d6a 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
          .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; }
          .reservation-details { background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; margin: 20px 0; }
          .detail-row { display: flex; justify-content: space-between; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #f0f0f0; }
          .detail-label { font-weight: bold; color: #2D6B47; }
          .rejected-badge { background: #dc3545; color: white; padding: 5px 15px; border-radius: 20px; display: inline-block; font-weight: bold; }
          .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0; color: #666; font-size: 12px; }
          .contact-info { background: #f0f7f2; padding: 15px; border-radius: 8px; margin: 20px 0; }
          .apology-box { background: #fff3cd; border-left: 4px solid #ffc107; padding: 20px; margin: 20px 0; border-radius: 8px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 style="margin: 0; font-size: 28px;">Reservation Update</h1>
        </div>
        
        <div class="content">
          <p>Dear <strong>${reservation.customer_name}</strong>,</p>
          
          <div class="apology-box">
            <p style="font-size: 18px; margin-bottom: 15px;">We regret to inform you that your reservation has been <span class="rejected-badge">CANCELLED</span>.</p>
            <p style="font-size: 16px;">Please accept our sincere apologies for the inconvenience caused.</p>
          </div>
          
          <div class="reservation-details">
            <h3 style="color: #dc3545; margin-top: 0;">Cancelled Reservation Details</h3>
            
            <div class="detail-row">
              <span class="detail-label">Date Requested:</span>
              <span>${formattedDate}</span>
            </div>
            
            <div class="detail-row">
              <span class="detail-label">Time Requested:</span>
              <span>${reservation.time || 'Not specified'}</span>
            </div>
            
            <div class="detail-row">
              <span class="detail-label">Number of Guests:</span>
              <span>${reservation.guests || 'Not specified'} people</span>
            </div>
            
            ${reservation.table_number ? `
            <div class="detail-row">
              <span class="detail-label">Table Number:</span>
              <span>${reservation.table_number}</span>
            </div>
            ` : ''}
          </div>
          
          <div class="contact-info">
            <h4 style="color: #2D6B47; margin-top: 0;">Contact Us for Assistance</h4>
            <p>📞 ${restaurantInfo.phone}</p>
            <p>✉️ ${restaurantInfo.email}</p>
          </div>
          
          <p>We hope to serve you on another occasion.</p>
          <p>The ${restaurantInfo.name} Team</p>
        </div>
        
        <div class="footer">
          <p>This is an automated message.</p>
        </div>
      </body>
      </html>
    `;
  };

  // ========== EXPORT FUNCTIONS ==========
  const prepareExportData = (reservationsToExport) => {
    return reservationsToExport.map(res => ({
      'Reservation ID': res.id,
      'Customer Name': res.customer_name || 'N/A',
      'Email': res.email || 'Not provided',
      'Phone': res.phone || 'N/A',
      'Date': res.date ? format(parseISO(res.date), 'dd/MM/yyyy') : 'N/A',
      'Time': res.time || 'N/A',
      'Guests': res.guests || 'N/A',
      'Table Number': res.table_number || 'Not assigned',
      'Status': res.status ? res.status.toUpperCase() : 'N/A',
      'Special Requests': res.special_requests || 'None',
      'Created At': res.created_at ? format(parseISO(res.created_at), 'dd/MM/yyyy HH:mm') : 'N/A'
    }));
  };

  const prepareDeletedExportData = (deletedReservationsToExport) => {
    return deletedReservationsToExport.map(res => ({
      'Original Reservation ID': res.original_reservation_id,
      'Customer Name': res.customer_name || 'N/A',
      'Email': res.email || 'Not provided',
      'Phone': res.phone || 'N/A',
      'Date': res.date ? format(parseISO(res.date), 'dd/MM/yyyy') : 'N/A',
      'Time': res.time || 'N/A',
      'Guests': res.guests || 'N/A',
      'Table Number': res.table_number || 'Not assigned',
      'Status': res.status ? res.status.toUpperCase() : 'N/A',
      'Special Requests': res.special_requests || 'None',
      'Deleted At': res.deleted_at ? format(parseISO(res.deleted_at), 'dd/MM/yyyy HH:mm') : 'N/A',
      'Deletion Reason': res.deletion_reason || 'N/A'
    }));
  };

  const prepareCustomersExportData = (customersToExport) => {
    return customersToExport.map(customer => ({
      'Customer ID': customer.id,
      'Name': customer.customer_name || 'N/A',
      'Email': customer.email || 'Not provided',
      'Phone': customer.phone || 'N/A',
      'Date of Birth': customer.date_of_birth ? format(parseISO(customer.date_of_birth), 'dd/MM/yyyy') : 'N/A',
      'Registration Date': customer.created_at ? format(parseISO(customer.created_at), 'dd/MM/yyyy HH:mm') : 'N/A',
      'Total Reservations': getCustomerReservationCount(customer.id),
      'Last Reservation': getLastReservationDate(customer.id)
    }));
  };

  // Excel Exports
  const exportToExcel = () => {
    try {
      const exportData = prepareExportData(filteredReservations);
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Reservations');
      const fileName = `haibeirut_reservations_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setTestResult({ success: true, message: ` Excel file exported successfully` });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: `Excel export failed: ${error.message}` });
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  const exportAllToExcel = () => {
    try {
      const exportData = prepareExportData(reservations);
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'All Reservations');
      const fileName = `haibeirut_all_reservations_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setTestResult({ success: true, message: ` All data exported successfully` });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: ` Export failed: ${error.message}` });
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  const exportDeletedToExcel = () => {
    try {
      const exportData = prepareDeletedExportData(deletedReservations);
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Deleted Reservations');
      const fileName = `haibeirut_deleted_reservations_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setTestResult({ success: true, message: ` Deleted reservations exported successfully` });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: ` Export failed: ${error.message}` });
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  const exportCustomersToExcel = () => {
    try {
      const exportData = prepareCustomersExportData(customers);
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Customers');
      const fileName = `haibeirut_customers_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setTestResult({ success: true, message: ` Customers exported successfully` });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: ` Export failed: ${error.message}` });
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  // PDF Exports
  const exportToPDF = () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(18);
      doc.setTextColor(139, 38, 53);
      doc.text('HAÏ Beirut - Reservations Report', 14, 15);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`, 14, 22);
      doc.text(`Total Reservations: ${filteredReservations.length}`, 14, 27);
      
      const tableData = filteredReservations.map(res => [
        res.customer_name || 'N/A',
        res.email || 'Not provided',
        res.phone || 'N/A',
        res.date ? format(parseISO(res.date), 'dd/MM/yyyy') : 'N/A',
        res.time || 'N/A',
        res.guests?.toString() || 'N/A',
        res.table_number || '—',
        res.status ? res.status.toUpperCase() : 'N/A'
      ]);

      autoTable(doc, {
        startY: 32,
        head: [['Name', 'Email', 'Phone', 'Date', 'Time', 'Guests', 'Table', 'Status']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [139, 38, 53], textColor: [255, 255, 255] }
      });

      const fileName = `haibeirut_reservations_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`;
      doc.save(fileName);
      setTestResult({ success: true, message: ` PDF file exported successfully` });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: ` PDF export failed: ${error.message}` });
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  const exportAllToPDF = () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(18);
      doc.setTextColor(139, 38, 53);
      doc.text('HAÏ Beirut - All Reservations Report', 14, 15);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`, 14, 22);
      doc.text(`Total Reservations: ${reservations.length}`, 14, 27);
      
      const tableData = reservations.map(res => [
        res.customer_name || 'N/A',
        res.email || 'Not provided',
        res.phone || 'N/A',
        res.date ? format(parseISO(res.date), 'dd/MM/yyyy') : 'N/A',
        res.time || 'N/A',
        res.guests?.toString() || 'N/A',
        res.table_number || '—',
        res.status ? res.status.toUpperCase() : 'N/A'
      ]);

      autoTable(doc, {
        startY: 32,
        head: [['Name', 'Email', 'Phone', 'Date', 'Time', 'Guests', 'Table', 'Status']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [139, 38, 53], textColor: [255, 255, 255] }
      });

      const fileName = `haibeirut_all_reservations_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`;
      doc.save(fileName);
      setTestResult({ success: true, message: ` All data exported to PDF successfully` });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: `PDF export failed: ${error.message}` });
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  const exportDeletedToPDF = () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(18);
      doc.setTextColor(139, 38, 53);
      doc.text('HAÏ Beirut - Deleted Reservations Report', 14, 15);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`, 14, 22);
      doc.text(`Total Deleted Reservations: ${deletedReservations.length}`, 14, 27);
      
      const tableData = deletedReservations.map(res => [
        res.customer_name || 'N/A',
        res.email || 'Not provided',
        res.phone || 'N/A',
        res.date ? format(parseISO(res.date), 'dd/MM/yyyy') : 'N/A',
        res.time || 'N/A',
        res.guests?.toString() || 'N/A',
        res.table_number || '—',
        res.status ? res.status.toUpperCase() : 'N/A',
        res.deleted_at ? format(parseISO(res.deleted_at), 'dd/MM/yyyy') : 'N/A',
        res.deletion_reason || 'N/A'
      ]);

      autoTable(doc, {
        startY: 32,
        head: [['Name', 'Email', 'Phone', 'Date', 'Time', 'Guests', 'Table', 'Status', 'Deleted', 'Reason']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [139, 38, 53], textColor: [255, 255, 255] }
      });

      const fileName = `haibeirut_deleted_reservations_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`;
      doc.save(fileName);
      setTestResult({ success: true, message: ` Deleted reservations exported to PDF successfully` });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: ` PDF export failed: ${error.message}` });
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  const exportCustomersToPDF = () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(18);
      doc.setTextColor(139, 38, 53);
      doc.text('HAÏ Beirut - Customers Report', 14, 15);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}`, 14, 22);
      doc.text(`Total Customers: ${customers.length}`, 14, 27);
      
      const tableData = customers.map(customer => [
        customer.customer_name || 'N/A',
        customer.email || 'Not provided',
        customer.phone || 'N/A',
        customer.date_of_birth ? format(parseISO(customer.date_of_birth), 'dd/MM/yyyy') : 'N/A',
        customer.created_at ? format(parseISO(customer.created_at), 'dd/MM/yyyy') : 'N/A',
        getCustomerReservationCount(customer.id).toString(),
        getLastReservationDate(customer.id)
      ]);

      autoTable(doc, {
        startY: 32,
        head: [['Name', 'Email', 'Phone', 'Date of Birth', 'Registered', 'Reservations', 'Last Visit']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [139, 38, 53], textColor: [255, 255, 255] }
      });

      const fileName = `haibeirut_customers_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`;
      doc.save(fileName);
      setTestResult({ success: true, message: `Customers exported to PDF successfully` });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: ` PDF export failed: ${error.message}` });
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  // Export Dropdown Component - Mobile Friendly
  const ExportDropdown = () => {
    return (
      <div className="relative">
        <Button
          variant="outline"
          onClick={() => setExportMenuOpen(!exportMenuOpen)}
          className="text-[#6B6B6B] hover:text-[#8B2635] hover:border-[#8B2635] w-full sm:w-auto text-sm"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Export</span>
          <ChevronDown className="w-4 h-4 ml-2" />
        </Button>
        
        <AnimatePresence>
          {exportMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute right-0 mt-2 w-64 sm:w-72 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-[#E5E5E5] overflow-hidden z-50"
            >
              <div className="py-1 max-h-96 overflow-y-auto">
                {/* ACTIVE RESERVATIONS */}
                <div className="px-3 py-2 bg-[#F9F9F9] border-b border-[#E5E5E5]">
                  <p className="text-xs font-medium text-[#6B6B6B]">ACTIVE RESERVATIONS</p>
                </div>
                
                <div className="grid grid-cols-2 gap-1 p-2">
                  <button
                    onClick={() => { exportToExcel(); setExportMenuOpen(false); }}
                    className="px-3 py-2 text-left text-sm text-[#2D2D2D] hover:bg-[#F5F5F5] rounded-lg flex flex-col items-center gap-1"
                  >
                    <FileSpreadsheet className="w-5 h-5 text-[#8B2635]" />
                    <span className="text-xs">Excel</span>
                    <span className="text-[10px] text-[#6B6B6B]">Filtered</span>
                  </button>
                  
                  <button
                    onClick={() => { exportToPDF(); setExportMenuOpen(false); }}
                    className="px-3 py-2 text-left text-sm text-[#2D2D2D] hover:bg-[#F5F5F5] rounded-lg flex flex-col items-center gap-1"
                  >
                    <FileText className="w-5 h-5 text-[#8B2635]" />
                    <span className="text-xs">PDF</span>
                    <span className="text-[10px] text-[#6B6B6B]">Filtered</span>
                  </button>
                </div>
                
                <button
                  onClick={() => { exportAllToExcel(); setExportMenuOpen(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-[#2D2D2D] hover:bg-[#F5F5F5] flex items-center gap-3 border-t border-[#E5E5E5]"
                >
                  <FileSpreadsheet className="w-4 h-4 text-[#8B2635]" />
                  <div>
                    <p className="font-medium">Export All to Excel</p>
                    <p className="text-xs text-[#6B6B6B]">All reservations (no filters)</p>
                  </div>
                </button>

                <button
                  onClick={() => { exportAllToPDF(); setExportMenuOpen(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-[#2D2D2D] hover:bg-[#F5F5F5] flex items-center gap-3"
                >
                  <FileText className="w-4 h-4 text-[#8B2635]" />
                  <div>
                    <p className="font-medium">Export All to PDF</p>
                    <p className="text-xs text-[#6B6B6B]">All reservations (no filters)</p>
                  </div>
                </button>

                {/* CUSTOMERS */}
                <div className="border-t border-[#E5E5E5] mt-2"></div>
                <div className="px-3 py-2 bg-[#F9F9F9] border-b border-[#E5E5E5]">
                  <p className="text-xs font-medium text-[#6B6B6B]">CUSTOMERS</p>
                </div>
                
                <div className="grid grid-cols-2 gap-1 p-2">
                  <button
                    onClick={() => { exportCustomersToExcel(); setExportMenuOpen(false); }}
                    className="px-3 py-2 text-left text-sm text-[#2D2D2D] hover:bg-[#F5F5F5] rounded-lg flex flex-col items-center gap-1"
                  >
                    <FileSpreadsheet className="w-5 h-5 text-[#8B2635]" />
                    <span className="text-xs">Excel</span>
                  </button>
                  
                  <button
                    onClick={() => { exportCustomersToPDF(); setExportMenuOpen(false); }}
                    className="px-3 py-2 text-left text-sm text-[#2D2D2D] hover:bg-[#F5F5F5] rounded-lg flex flex-col items-center gap-1"
                  >
                    <FileText className="w-5 h-5 text-[#8B2635]" />
                    <span className="text-xs">PDF</span>
                  </button>
                </div>

                {/* ARCHIVED */}
                <div className="border-t border-[#E5E5E5] mt-2"></div>
                <div className="px-3 py-2 bg-[#F9F9F9] border-b border-[#E5E5E5]">
                  <p className="text-xs font-medium text-[#6B6B6B]">ARCHIVED</p>
                </div>
                
                <div className="grid grid-cols-2 gap-1 p-2">
                  <button
                    onClick={() => { exportDeletedToExcel(); setExportMenuOpen(false); }}
                    className="px-3 py-2 text-left text-sm text-[#2D2D2D] hover:bg-[#F5F5F5] rounded-lg flex flex-col items-center gap-1"
                  >
                    <FileSpreadsheet className="w-5 h-5 text-[#8B2635]" />
                    <span className="text-xs">Excel</span>
                  </button>
                  
                  <button
                    onClick={() => { exportDeletedToPDF(); setExportMenuOpen(false); }}
                    className="px-3 py-2 text-left text-sm text-[#2D2D2D] hover:bg-[#F5F5F5] rounded-lg flex flex-col items-center gap-1"
                  >
                    <FileText className="w-5 h-5 text-[#8B2635]" />
                    <span className="text-xs">PDF</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // ========== BIRTHDAY FUNCTIONS ==========
  const getTodaysBirthdays = () => {
    const today = new Date();
    return customers.filter(customer => {
      if (!customer.date_of_birth) return false;
      try {
        const birthDate = new Date(customer.date_of_birth);
        return birthDate.getDate() === today.getDate() && birthDate.getMonth() === today.getMonth();
      } catch { return false; }
    });
  };

  const getBirthdaysByDate = (dateStr) => {
    const selected = new Date(dateStr);
    return customers.filter(customer => {
      if (!customer.date_of_birth) return false;
      try {
        const birthDate = new Date(customer.date_of_birth);
        return birthDate.getDate() === selected.getDate() && birthDate.getMonth() === selected.getMonth();
      } catch { return false; }
    });
  };

  const getMonthBirthdays = (dateStr) => {
    const selected = new Date(dateStr);
    const selectedMonth = selected.getMonth();
    return customers.filter(customer => {
      if (!customer.date_of_birth) return false;
      try {
        const birthDate = new Date(customer.date_of_birth);
        return birthDate.getMonth() === selectedMonth;
      } catch { return false; }
    });
  };

  const groupBirthdaysByDate = (birthdays) => {
    const grouped = {};
    birthdays.forEach(customer => {
      if (!customer.date_of_birth) return;
      try {
        const birthDate = new Date(customer.date_of_birth);
        const dateKey = format(birthDate, "yyyy-MM-dd");
        const displayDate = format(birthDate, "MMMM d");
        if (!grouped[dateKey]) grouped[dateKey] = { date: displayDate, customers: [] };
        grouped[dateKey].customers.push(customer);
      } catch { }
    });
    return Object.values(grouped).sort((a, b) => {
      const dateA = new Date(a.customers[0].date_of_birth);
      const dateB = new Date(b.customers[0].date_of_birth);
      return dateA.getDate() - dateB.getDate();
    });
  };

  const formatDateOfBirth = (dobString) => {
    if (!dobString) return '—';
    try { return format(new Date(dobString), "dd/MM/yyyy"); }
    catch { return dobString; }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try { return format(parseISO(dateString), 'dd/MM/yyyy'); }
    catch { return dateString; }
  };

  const getCustomerReservationCount = (customerId) => {
    return reservations.filter(r => r.customer_id === customerId).length;
  };

  const getLastReservationDate = (customerId) => {
    const customerReservations = reservations
      .filter(r => r.customer_id === customerId)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    if (customerReservations.length === 0) return 'Never';
    try { return format(parseISO(customerReservations[0].date), 'dd/MM/yyyy'); }
    catch { return 'Unknown'; }
  };

  const todaysBirthdays = getTodaysBirthdays();

  // ========== HANDLE TABLE NUMBER UPDATE ==========
  const handleUpdateTableNumber = async (id, tableNumber) => {
    setProcessingId(id);
    try {
      await updateTableNumberMutation.mutateAsync({ id, table_number: tableNumber });
      setTestResult({ success: true, message: ` Table number updated successfully` });
      setTimeout(() => setTestResult(null), 2000);
    } catch (error) {
      setTestResult({ success: false, message: `Failed to update table number: ${error.message}` });
      setTimeout(() => setTestResult(null), 3000);
    } finally { setProcessingId(null); }
  };

  // ========== HANDLE DELETE WITH REASON ==========
  const handleDeleteWithReason = (reservation) => {
    setPendingDeletion(reservation);
    setShowDeletionReasonModal(true);
  };

  const handleConfirmDeletion = async (reason) => {
    if (!pendingDeletion) return;
    setProcessingId(pendingDeletion.id);
    setShowDeletionReasonModal(false);
    try {
      await deleteMutation.mutateAsync({ id: pendingDeletion.id, reason });
    } catch (error) {
    } finally {
      setPendingDeletion(null);
      setProcessingId(null);
    }
  };

  // ========== HANDLE RESTORE ==========
  const handleRestore = async (deletedReservation) => {
    if (!window.confirm("Are you sure you want to restore this reservation?")) return;
    await restoreMutation.mutateAsync(deletedReservation);
  };

  // ========== HANDLE PERMANENT DELETE ==========
  const handlePermanentDelete = async (deletedReservation) => {
    if (!window.confirm("⚠️ This action cannot be undone! Are you sure?")) return;
    try {
      const { error } = await supabase
        .from('deleted_reservations')
        .delete()
        .eq('id', deletedReservation.id);
      if (error) throw error;
      
      const { data } = await supabase
        .from('deleted_reservations')
        .select('*')
        .order('deleted_at', { ascending: false });
      setDeletedReservations(data || []);
      
      setTestResult({ success: true, message: " Reservation permanently deleted" });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: ` Failed to delete: ${error.message}` });
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  // ========== BIRTHDAY MODAL - Mobile Friendly ==========
  const BirthdayModal = () => {
    const monthBirthdays = getMonthBirthdays(selectedDate);
    const groupedMonthBirthdays = groupBirthdaysByDate(monthBirthdays);
    const selectedDayBirthdays = getBirthdaysByDate(selectedDate);
    
    return (
      <AnimatePresence>
        {showBirthdayModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={() => setShowBirthdayModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-4xl lg:max-w-6xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-gradient-to-r from-[#8B2635] to-[#B23B4A] text-white p-3 sm:p-6 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <PartyPopper className="w-5 h-5 sm:w-6 sm:h-6" />
                    <h2 className="text-base sm:text-xl font-bold">Birthday Celebrations</h2>
                  </div>
                  <button onClick={() => setShowBirthdayModal(false)} className="p-2 hover:bg-white/20 rounded-full">
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
                  <div className="bg-[#F9F9F9] p-2 sm:p-4 rounded-lg border border-[#E5E5E5]">
                    <p className="text-[10px] sm:text-xs text-[#6B6B6B]">Total Customers</p>
                    <p className="text-base sm:text-2xl font-bold text-[#2D2D2D]">{customers.length}</p>
                  </div>
                  <div className="bg-[#F9F9F9] p-2 sm:p-4 rounded-lg border border-[#E5E5E5]">
                    <p className="text-[10px] sm:text-xs text-[#6B6B6B]">Today's Birthdays</p>
                    <p className="text-base sm:text-2xl font-bold text-[#8B2635]">{todaysBirthdays.length}</p>
                  </div>
                  <div className="bg-[#F9F9F9] p-2 sm:p-4 rounded-lg border border-[#E5E5E5]">
                    <p className="text-[10px] sm:text-xs text-[#6B6B6B]">This Month</p>
                    <p className="text-base sm:text-2xl font-bold text-[#2D2D2D]">{monthBirthdays.length}</p>
                  </div>
                  <div className="bg-[#F9F9F9] p-2 sm:p-4 rounded-lg border border-[#E5E5E5]">
                    <p className="text-[10px] sm:text-xs text-[#6B6B6B]">Selected Date</p>
                    <p className="text-base sm:text-2xl font-bold text-[#8B2635]">{selectedDayBirthdays.length}</p>
                  </div>
                </div>

                <div className="mb-4 sm:mb-6">
                  <label className="block text-xs sm:text-sm font-medium text-[#6B6B6B] mb-2">Select Month to View</label>
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-[#8B2635]" />
                    <input
                      type="month"
                      value={selectedDate.slice(0, 7)}
                      onChange={(e) => setSelectedDate(e.target.value + '-01')}
                      className="flex-1 p-2 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Today's Birthdays */}
                  <div>
                    <h3 className="text-sm sm:text-lg font-semibold text-[#2D2D2D] mb-3 sm:mb-4 flex items-center gap-2 sticky top-0 bg-white py-2">
                      <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-[#8B2635]" />
                      <span>Today's Birthdays</span>
                      <span className="ml-2 px-2 py-1 bg-[#8B2635]/10 text-[#8B2635] rounded-full text-xs">
                        {todaysBirthdays.length}
                      </span>
                    </h3>
                    
                    {loadingCustomers ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-[#8B2635]" />
                      </div>
                    ) : todaysBirthdays.length > 0 ? (
                      <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                        {todaysBirthdays.map(customer => (
                          <div key={customer.id} className="bg-gradient-to-r from-[#FFF0F0] to-[#FFE4E4] p-3 sm:p-5 rounded-lg border border-[#FFD1D1]">
                            <div className="flex items-start gap-3 sm:gap-4">
                              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#8B2635] rounded-full flex items-center justify-center flex-shrink-0">
                                <Cake className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-[#2D2D2D] text-sm sm:text-base break-words">{customer.customer_name}</p>
                                <div className="mt-2 space-y-2">
                                  <div className="flex items-center gap-2 text-xs sm:text-sm text-[#6B6B6B]">
                                    <Phone className="w-3 h-3 flex-shrink-0" /> 
                                    <span className="break-words">{customer.phone}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs sm:text-sm text-[#8B2635] bg-[#8B2635]/10 p-2 rounded-lg flex-wrap">
                                    <Mail className="w-3 h-3 flex-shrink-0" /> 
                                    <span className="truncate flex-1 min-w-0">{customer.email || 'Not provided'}</span>
                                    {customer.email && (
                                      <button
                                        onClick={() => copyEmailToClipboard(customer.email)}
                                        className="ml-auto hover:bg-[#8B2635]/20 p-1 rounded flex-shrink-0"
                                      >
                                        {copiedEmail === customer.email ? 
                                          <Check className="w-3 h-3" /> : 
                                          <Copy className="w-3 h-3" />
                                        }
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 sm:py-8 bg-[#F9F9F9] rounded-lg border-dashed border border-[#E5E5E5]">
                        <Cake className="w-8 h-8 sm:w-12 sm:h-12 text-[#CCCCCC] mx-auto mb-2" />
                        <p className="text-sm sm:text-base text-[#6B6B6B]">No birthdays today</p>
                      </div>
                    )}
                  </div>

                  {/* Month Birthdays */}
                  <div>
                    <h3 className="text-sm sm:text-lg font-semibold text-[#2D2D2D] mb-3 sm:mb-4 flex items-center gap-2 sticky top-0 bg-white py-2">
                      <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-[#8B2635]" />
                      <span>Birthdays in {format(new Date(selectedDate), "MMMM yyyy")}</span>
                      <span className="ml-2 px-2 py-1 bg-[#8B2635]/10 text-[#8B2635] rounded-full text-xs">
                        {monthBirthdays.length}
                      </span>
                    </h3>
                    
                    {groupedMonthBirthdays.length > 0 ? (
                      <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                        {groupedMonthBirthdays.map((group, index) => (
                          <div key={index} className="bg-white rounded-lg border border-[#E5E5E5] overflow-hidden">
                            <div className="bg-[#F9F9F9] px-3 sm:px-4 py-2 border-b">
                              <p className="text-sm sm:text-base font-medium text-[#8B2635]">
                                {group.date} • {group.customers.length} {group.customers.length === 1 ? 'birthday' : 'birthdays'}
                              </p>
                            </div>
                            <div className="p-3 sm:p-4 space-y-3">
                              {group.customers.map(customer => (
                                <div key={customer.id} className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 border-b border-[#E5E5E5] last:border-0 pb-3 last:pb-0">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-[#2D2D2D] text-sm break-words">{customer.customer_name}</p>
                                    <div className="flex items-center gap-2 mt-1 text-xs text-[#6B6B6B]">
                                      <Phone className="w-3 h-3 flex-shrink-0" /> 
                                      <span className="break-words">{customer.phone}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1 text-xs text-[#8B2635] flex-wrap">
                                      <Mail className="w-3 h-3 flex-shrink-0" /> 
                                      <span className="truncate min-w-0">{customer.email || 'Not provided'}</span>
                                      {customer.email && (
                                        <button
                                          onClick={() => copyEmailToClipboard(customer.email)}
                                          className="ml-1 hover:bg-[#8B2635]/10 p-1 rounded flex-shrink-0"
                                        >
                                          {copiedEmail === customer.email ? 
                                            <Check className="w-3 h-3" /> : 
                                            <Copy className="w-3 h-3" />
                                          }
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <p className="text-sm font-medium text-[#8B2635] sm:text-right whitespace-nowrap flex-shrink-0">
                                    {formatDateOfBirth(customer.date_of_birth)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 sm:py-8 bg-[#F9F9F9] rounded-lg border-dashed border border-[#E5E5E5]">
                        <CalendarDays className="w-8 h-8 sm:w-12 sm:h-12 text-[#CCCCCC] mx-auto mb-2" />
                        <p className="text-sm sm:text-base text-[#6B6B6B]">No birthdays this month</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  // ========== CUSTOMERS MODAL - Mobile Friendly ==========
  const CustomersModal = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const filteredCustomers = customers.filter(customer => {
      const searchLower = searchTerm.toLowerCase();
      return (
        customer.customer_name?.toLowerCase().includes(searchLower) ||
        customer.email?.toLowerCase().includes(searchLower) ||
        customer.phone?.toLowerCase().includes(searchLower)
      );
    });

    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
    const paginatedCustomers = filteredCustomers.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

    React.useEffect(() => { setCurrentPage(1); }, [searchTerm]);

    return (
      <AnimatePresence>
        {showCustomersModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={() => setShowCustomersModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-gradient-to-r from-[#8B2635] to-[#B23B4A] text-white p-3 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Users className="w-5 h-5 sm:w-6 sm:h-6" />
                    <h2 className="text-base sm:text-xl font-bold">Customers</h2>
                  </div>
                  <button onClick={() => setShowCustomersModal(false)} className="p-2 hover:bg-white/20 rounded-full">
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40"
                  />
                </div>

                <div className="space-y-3">
                  {loadingCustomers ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-[#8B2635] mx-auto" />
                    </div>
                  ) : paginatedCustomers.length > 0 ? (
                    paginatedCustomers.map((customer) => (
                      <div key={customer.id} className="bg-white border border-[#E5E5E5] rounded-lg p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#2D2D2D] break-words">{customer.customer_name}</p>
                            <div className="mt-2 space-y-1 text-sm">
                              <div className="flex items-center gap-2 text-[#6B6B6B] flex-wrap">
                                <Mail className="w-3 h-3 flex-shrink-0" /> 
                                <span className="truncate min-w-0">{customer.email || 'Not provided'}</span>
                              </div>
                              <div className="flex items-center gap-2 text-[#6B6B6B]">
                                <Phone className="w-3 h-3 flex-shrink-0" /> 
                                <span className="break-words">{customer.phone}</span>
                              </div>
                              {customer.date_of_birth && (
                                <div className="flex items-center gap-2 text-[#8B2635]">
                                  <Cake className="w-3 h-3 flex-shrink-0" /> 
                                  <span>{formatDateOfBirth(customer.date_of_birth)}</span>
                                </div>
                              )}
                              <div className="flex flex-wrap items-center gap-2 text-xs text-[#6B6B6B] mt-2">
                                <span>Reservations: {getCustomerReservationCount(customer.id)}</span>
                                <span>•</span>
                                <span>Last: {getLastReservationDate(customer.id)}</span>
                              </div>
                            </div>
                          </div>
                          {customer.email && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[#8B2635] border-[#8B2635] flex-shrink-0 self-start"
                              onClick={() => copyEmailToClipboard(customer.email)}
                            >
                              {copiedEmail === customer.email ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-[#6B6B6B]">No customers found</div>
                  )}
                </div>

                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between mt-4 pt-4 border-t gap-2">
                    <p className="text-sm text-[#6B6B6B]">
                      Page {currentPage} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-2 border rounded-lg disabled:opacity-50 min-w-[36px] min-h-[36px] flex items-center justify-center"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 border rounded-lg disabled:opacity-50 min-w-[36px] min-h-[36px] flex items-center justify-center"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  // ========== HANDLERS ==========
  const handleApprove = async (reservation) => {
    setProcessingId(reservation.id);
    setEmailStatus(prev => ({ ...prev, [reservation.id]: { loading: true } }));
    try {
      await updateMutation.mutateAsync({ id: reservation.id, data: { status: 'approved', approved_at: new Date().toISOString() } });
      const { data: updatedReservation } = await supabase
        .from('reservations')
        .select('*, customers(*)')
        .eq('id', reservation.id)
        .single();
      
      if (updatedReservation && updatedReservation.email && updatedReservation.email.trim() !== '') {
        await sendEmailMutation.mutateAsync({ reservationId: reservation.id, reservation: updatedReservation, action: 'approve' });
      } else if (updatedReservation) {
        setEmailStatus(prev => ({ 
          ...prev, 
          [reservation.id]: { 
            success: true, 
            message: 'Approved (no email to send)' 
          } 
        }));
      }
    } catch (error) {
      setEmailStatus(prev => ({ ...prev, [reservation.id]: { success: false, message: `Failed: ${error.message}` } }));
    } finally { setProcessingId(null); }
  };

  const handleReject = async (reservation) => {
    if (!window.confirm(`Reject ${reservation.customer_name}'s reservation?`)) return;
    setProcessingId(reservation.id);
    setEmailStatus(prev => ({ ...prev, [reservation.id]: { loading: true } }));
    try {
      await updateMutation.mutateAsync({ id: reservation.id, data: { status: 'rejected', rejected_at: new Date().toISOString() } });
      const { data: updatedReservation } = await supabase
        .from('reservations')
        .select('*, customers(*)')
        .eq('id', reservation.id)
        .single();
      
      if (updatedReservation && updatedReservation.email && updatedReservation.email.trim() !== '') {
        await sendEmailMutation.mutateAsync({ reservationId: reservation.id, reservation: updatedReservation, action: 'reject' });
      } else if (updatedReservation) {
        setEmailStatus(prev => ({ 
          ...prev, 
          [reservation.id]: { 
            success: true, 
            message: 'Rejected (no email to send)' 
          } 
        }));
      }
    } catch (error) {
      setEmailStatus(prev => ({ ...prev, [reservation.id]: { success: false, message: `Failed: ${error.message}` } }));
    } finally { setProcessingId(null); }
  };

  const handleSendEmailOnly = async (reservation) => {
    if (!reservation.email || reservation.email.trim() === '') {
      setEmailStatus(prev => ({
        ...prev,
        [reservation.id]: { success: false, message: 'No email address provided' }
      }));
      return;
    }

    setEmailStatus(prev => ({ ...prev, [reservation.id]: { loading: true } }));
    try {
      await sendEmailMutation.mutateAsync({
        reservationId: reservation.id,
        reservation: reservation,
        action: reservation.status === 'approved' ? 'approve' : 'reject'
      });
    } catch (error) {
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setDateFilter("");
    setMonthFilter("");
    setYearFilter("");
    setTimeFilter("");
    setStatusFilter("all");
  };

  // Filter reservations
  const filteredReservations = reservations.filter(res => {
    const matchesDate = !dateFilter || res.date === dateFilter;
    let matchesMonth = true;
    if (monthFilter && res.date) {
      const resMonth = format(parseISO(res.date), 'MM');
      matchesMonth = resMonth === monthFilter;
    }
    let matchesYear = true;
    if (yearFilter && res.date) {
      const resYear = format(parseISO(res.date), 'yyyy');
      matchesYear = resYear === yearFilter;
    }
    const matchesTime = !timeFilter || res.time === timeFilter;
    const matchesStatus = statusFilter === "all" || res.status === statusFilter;
    return matchesDate && matchesMonth && matchesYear && matchesTime && matchesStatus;
  });

  const isFilterActive = dateFilter || monthFilter || yearFilter || timeFilter || statusFilter !== "all";

  const stats = {
    pending: reservations.filter(r => r.status === 'pending').length,
    approved: reservations.filter(r => r.status === 'approved').length,
    rejected: reservations.filter(r => r.status === 'rejected').length
  };

  if (authError) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="bg-red-50 text-red-700 p-6 rounded-xl border border-red-200 mb-4">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-500" />
            <h2 className="text-lg font-bold mb-2">Authentication Error</h2>
            <p className="text-sm">{authError}</p>
          </div>
          <p className="text-sm text-[#6B6B6B]">Redirecting to login page...</p>
        </div>
      </div>
    );
  }

  if (authChecking) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#8B2635] mx-auto mb-4" />
          <p className="text-base text-[#6B6B6B]">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!adminUser) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#8B2635] mx-auto mb-4" />
          <p className="text-sm text-[#6B6B6B]">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      <BirthdayModal />
      <CustomersModal />
      
      <AnimatePresence>
        {showBlockedDatesModal && (
          <BlockedDatesManager onClose={() => setShowBlockedDatesModal(false)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeletedModal && (
          <DeletedReservationsModal
            deletedReservations={deletedReservations}
            loading={loadingDeleted}
            onClose={() => setShowDeletedModal(false)}
            onRestore={handleRestore}
            onPermanentDelete={handlePermanentDelete}
            formatDate={formatDate}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeletionReasonModal && pendingDeletion && (
          <DeletionReasonModal
            reservation={pendingDeletion}
            onClose={() => { setShowDeletionReasonModal(false); setPendingDeletion(null); }}
            onConfirm={handleConfirmDeletion}
          />
        )}
      </AnimatePresence>

      {/* ============================================================ */}
      {/* MOBILE-FRIENDLY NOTIFICATION TOASTS */}
      {/* ============================================================ */}
      <div className="fixed top-0 left-0 right-0 z-[100] pointer-events-none p-3 sm:p-4 space-y-3">
        <AnimatePresence>
          {/* Email Success Toast */}
          {showEmailSuccess && (
            <motion.div 
              initial={{ opacity: 0, y: -50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.9 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="pointer-events-auto mx-auto max-w-sm sm:max-w-md w-full"
            >
              <div className="bg-emerald-50 text-emerald-700 px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-xl border border-emerald-200 flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm sm:text-base">Success!</p>
                  <p className="text-xs sm:text-sm text-emerald-600 truncate">Email sent successfully</p>
                </div>
                <button
                  onClick={() => setShowEmailSuccess(false)}
                  className="flex-shrink-0 p-1 hover:bg-emerald-100 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-emerald-600" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Email Error Toast */}
          {showEmailError && (
            <motion.div 
              initial={{ opacity: 0, y: -50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.9 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="pointer-events-auto mx-auto max-w-sm sm:max-w-md w-full"
            >
              <div className="bg-red-50 text-red-700 px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-xl border border-red-200 flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm sm:text-base">Error!</p>
                  <p className="text-xs sm:text-sm text-red-600 truncate">Failed to send email</p>
                </div>
                <button
                  onClick={() => setShowEmailError(false)}
                  className="flex-shrink-0 p-1 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Test Result Toast */}
          {testResult && (
            <motion.div 
              initial={{ opacity: 0, y: -50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.9 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="pointer-events-auto mx-auto max-w-sm sm:max-w-md w-full"
            >
              <div className={`px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-xl border flex items-start gap-3 ${
                testResult.success 
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}>
                <div className="flex-shrink-0 mt-0.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    testResult.success ? 'bg-emerald-100' : 'bg-red-100'
                  }`}>
                    {testResult.success ? (
                      <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm sm:text-base">
                    {testResult.success ? 'Success!' : 'Error!'}
                  </p>
                  <p className="text-xs sm:text-sm break-words">
                    {testResult.message}
                  </p>
                </div>
                <button
                  onClick={() => setTestResult(null)}
                  className={`flex-shrink-0 p-1 rounded-lg transition-colors ${
                    testResult.success ? 'hover:bg-emerald-100' : 'hover:bg-red-100'
                  }`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ============================================================ */}
      {/* HEADER */}
      {/* ============================================================ */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-10">
        <div className="px-3 sm:px-6 py-2 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo / Brand */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#8B2635] rounded-lg flex items-center justify-center flex-shrink-0">
                <LayoutDashboard className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-xl font-bold text-[#2D2D2D] truncate">Haibeirut Admin</h1>
                <p className="text-[10px] sm:text-sm text-[#6B6B6B] hidden xs:block">Reservation Management</p>
              </div>
            </div>

            {/* Desktop Navigation - Hidden on mobile */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-4 flex-wrap">
              <Button variant="outline" onClick={() => setShowDeletedModal(true)} className="relative text-sm">
                <Archive className="w-4 h-4 mr-1 xl:mr-2" />
                <span className="hidden xl:inline">Deleted</span>
                {deletedReservations.length > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#8B2635] text-white text-xs rounded-full flex items-center justify-center">
                    {deletedReservations.length}
                  </span>
                )}
              </Button>

              <Button variant="outline" onClick={() => setShowCustomersModal(true)} className="text-sm">
                <Users className="w-4 h-4 mr-1 xl:mr-2" />
                <span className="hidden xl:inline">Customers</span>
              </Button>

              <Button variant="outline" onClick={() => setShowBlockedDatesModal(true)} className="text-sm">
                <CalendarClock className="w-4 h-4 mr-1 xl:mr-2" />
                <span className="hidden xl:inline">Blocked</span>
              </Button>

              <Button 
                variant="outline" 
                onClick={() => setShowBirthdayModal(true)}
                className={`text-sm ${todaysBirthdays.length > 0 ? 'border-[#8B2635] bg-[#8B2635]/5' : ''}`}
              >
                <Cake className="w-4 h-4 mr-1 xl:mr-2" />
                <span className="hidden xl:inline">Birthdays</span>
                {todaysBirthdays.length > 0 && (
                  <span className="ml-1 xl:ml-2 px-2 py-0.5 bg-[#8B2635] text-white text-xs rounded-full">
                    {todaysBirthdays.length}
                  </span>
                )}
              </Button>

              <ExportDropdown />

              {adminUser && (
                <div className="flex items-center gap-2 text-sm text-[#6B6B6B] border-r border-[#E5E5E5] pr-4">
                  <User className="w-4 h-4" />
                  <span className="font-medium truncate max-w-[120px] xl:max-w-[200px]">{adminUser.email}</span>
                </div>
              )}
              
              <Button variant="outline" onClick={handleLogout} className="text-red-600 hover:text-red-700 text-sm">
                <LogOut className="w-4 h-4 mr-1 xl:mr-2" />
                <span className="hidden xl:inline">Logout</span>
              </Button>
            </div>

            {/* Mobile Menu Button - Always visible on mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex lg:hidden p-2 hover:bg-[#F5F5F5] rounded-lg transition-colors -mr-2"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-[#2D2D2D]" />
              ) : (
                <Menu className="w-5 h-5 text-[#2D2D2D]" />
              )}
            </button>
          </div>
        </div>

        {/* ============================================================ */}
        {/* MOBILE MENU */}
        {/* ============================================================ */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="lg:hidden border-t border-[#E5E5E5] bg-white shadow-lg overflow-hidden"
            >
              <div className="p-4 space-y-4">
                {/* Quick Action Grid - 3 columns on mobile */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => { setShowDeletedModal(true); setMobileMenuOpen(false); }}
                    className="flex flex-col items-center justify-center p-3 bg-[#F9F9F9] rounded-xl border border-[#E5E5E5] hover:border-[#8B2635] transition-all active:scale-95"
                  >
                    <div className="relative">
                      <Archive className="w-6 h-6 text-[#8B2635] mb-1" />
                      {deletedReservations.length > 0 && (
                        <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#8B2635] text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                          {deletedReservations.length}
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-medium text-[#2D2D2D]">Deleted</span>
                  </button>

                  <button
                    onClick={() => { setShowCustomersModal(true); setMobileMenuOpen(false); }}
                    className="flex flex-col items-center justify-center p-3 bg-[#F9F9F9] rounded-xl border border-[#E5E5E5] hover:border-[#8B2635] transition-all active:scale-95"
                  >
                    <Users className="w-6 h-6 text-[#8B2635] mb-1" />
                    <span className="text-xs font-medium text-[#2D2D2D]">Customers</span>
                  </button>

                  <button
                    onClick={() => { setShowBlockedDatesModal(true); setMobileMenuOpen(false); }}
                    className="flex flex-col items-center justify-center p-3 bg-[#F9F9F9] rounded-xl border border-[#E5E5E5] hover:border-[#8B2635] transition-all active:scale-95"
                  >
                    <CalendarClock className="w-6 h-6 text-[#8B2635] mb-1" />
                    <span className="text-xs font-medium text-[#2D2D2D]">Blocked</span>
                  </button>

                  <button
                    onClick={() => { setShowBirthdayModal(true); setMobileMenuOpen(false); }}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all active:scale-95 ${
                      todaysBirthdays.length > 0 
                        ? 'bg-[#8B2635]/10 border-[#8B2635]' 
                        : 'bg-[#F9F9F9] border-[#E5E5E5] hover:border-[#8B2635]'
                    }`}
                  >
                    <div className="relative">
                      <Cake className="w-6 h-6 text-[#8B2635] mb-1" />
                      {todaysBirthdays.length > 0 && (
                        <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#8B2635] text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                          {todaysBirthdays.length}
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-medium text-[#2D2D2D]">Birthdays</span>
                  </button>

                  {/* Export button - spans 2 columns */}
                  <div className="col-span-2">
                    <ExportDropdown />
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-[#E5E5E5]"></div>

                {/* User Info & Actions */}
                {adminUser && (
                  <div className="flex items-center gap-2 text-sm text-[#6B6B6B] bg-[#F9F9F9] p-3 rounded-xl">
                    <User className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium truncate">{adminUser.email}</span>
                  </div>
                )}

                {/* Logout button - Full width on mobile */}
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 justify-center"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content - Full Width */}
      <main className="px-3 sm:px-6 py-3 sm:py-8">
        {/* Stats Cards */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="bg-white rounded-lg p-2 sm:p-4 border border-[#E5E5E5] shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-[#6B6B6B]">Pending</p>
                <p className="text-lg sm:text-xl font-bold text-[#2D2D2D]">{stats.pending}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-2 sm:p-4 border border-[#E5E5E5] shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-[#6B6B6B]">Approved</p>
                <p className="text-lg sm:text-xl font-bold text-[#2D2D2D]">{stats.approved}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-2 sm:p-4 border border-[#E5E5E5] shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <XCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-[#6B6B6B]">Rejected</p>
                <p className="text-lg sm:text-xl font-bold text-[#2D2D2D]">{stats.rejected}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Table Container - Full Width */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl border border-[#E5E5E5] shadow-sm overflow-hidden">
          {/* Filters */}
          <div className="p-3 sm:p-4 border-b border-[#E5E5E5]">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#6B6B6B]" />
                <h3 className="font-medium text-[#2D2D2D] text-sm sm:text-base">Filters</h3>
                {isFilterActive && <span className="px-2 py-0.5 bg-[#8B2635]/10 text-[#8B2635] rounded-full text-xs">Active</span>}
              </div>
              <div className="flex gap-2">
                {isFilterActive && (
                  <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-[#8B2635] text-xs sm:text-sm">
                    <X className="w-3 h-3 mr-1" /> Clear All
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => refetch()} className="text-xs sm:text-sm">
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Filter Controls */}
          <div className="p-3 sm:p-4 border-b border-[#E5E5E5]">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
              <div>
                <label className="block text-[10px] sm:text-xs font-medium text-[#6B6B6B] mb-1">Date</label>
                <input 
                  type="date" 
                  value={dateFilter} 
                  onChange={(e) => setDateFilter(e.target.value)} 
                  className="w-full p-1.5 sm:p-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40" 
                />
              </div>
              <div>
                <label className="block text-[10px] sm:text-xs font-medium text-[#6B6B6B] mb-1">Month</label>
                <select 
                  value={monthFilter} 
                  onChange={(e) => setMonthFilter(e.target.value)} 
                  className="w-full p-1.5 sm:p-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40"
                >
                  <option value="">All</option>
                  {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] sm:text-xs font-medium text-[#6B6B6B] mb-1">Year</label>
                <select 
                  value={yearFilter} 
                  onChange={(e) => setYearFilter(e.target.value)} 
                  className="w-full p-1.5 sm:p-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40"
                >
                  <option value="">All</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] sm:text-xs font-medium text-[#6B6B6B] mb-1">Time</label>
                <select 
                  value={timeFilter} 
                  onChange={(e) => setTimeFilter(e.target.value)} 
                  className="w-full p-1.5 sm:p-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40"
                >
                  <option value="">All</option>
                  {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-[10px] sm:text-xs font-medium text-[#6B6B6B] mb-1">Status</label>
                <select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)} 
                  className="w-full p-1.5 sm:p-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B2635]/40"
                >
                  <option value="all">All ({reservations.length})</option>
                  <option value="pending">Pending ({stats.pending})</option>
                  <option value="approved">Approved ({stats.approved})</option>
                  <option value="rejected">Rejected ({stats.rejected})</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="px-3 sm:px-4 py-2 sm:py-3 bg-[#F9F9F9] border-b border-[#E5E5E5]">
            <p className="text-xs sm:text-sm text-[#2D2D2D]">
              Showing {filteredReservations.length} of {reservations.length} reservations
            </p>
          </div>

          {/* Table */}
          {reservationsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#8B2635]" />
            </div>
          ) : (
            <ReservationTable
              reservations={filteredReservations}
              onApprove={handleApprove}
              onReject={handleReject}
              onDelete={handleDeleteWithReason}
              onSendEmail={handleSendEmailOnly}
              onUpdateTableNumber={handleUpdateTableNumber}
              processingId={processingId}
              emailStatus={emailStatus}
            />
          )}
        </motion.div>
      </main>
    </div>
  );
}